import { LinearProgress, Toolbar, alpha } from '@mui/material'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import { styled, useTheme } from '@mui/material/styles'
import React from 'react'
import { useNetworkStatus } from '../hooks'
import AppHeaderControls, { getNetworkHeaderColor } from './AppHeaderControls'

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

interface HeaderProps {
  open: boolean
  handleDrawerOpen: () => void
  isLoaderActive: boolean
  titleBarOffset?: number
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => {
  const isDark = theme.palette.mode === 'dark'
  return {
    zIndex: theme.zIndex.drawer + 1,
    backdropFilter: 'blur(30px) saturate(200%)',
    borderBottom: `1px solid ${alpha(isDark ? theme.palette.common.white : theme.palette.common.black, isDark ? 0.1 : 0.08)}`,
    boxShadow: `0 18px 42px ${alpha(theme.palette.common.black, isDark ? 0.32 : 0.08)}`,
    width: '100%',
  }
})

const Header: React.FC<HeaderProps> = ({
  open,
  handleDrawerOpen,
  isLoaderActive,
  titleBarOffset = 0,
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const accentColor = isDark ? theme.palette.primary.main : theme.palette.secondary.main
  const glassBg = alpha(theme.palette.background.paper, isDark ? 0.72 : 0.88)
  const { isOnline, effectiveType } = useNetworkStatus()
  const headerBackgroundColor = getNetworkHeaderColor(theme, isOnline, effectiveType, glassBg)

  return (
    <AppBar
      position="fixed"
      open={open}
      sx={{
        background: isDark
          ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.92)} 0%, ${alpha(theme.palette.primary.main, 0.22)} 100%)`
          : `linear-gradient(135deg, ${headerBackgroundColor} 0%, ${alpha(theme.palette.primary.main, 0.42)} 100%)`,
        transition: 'background-color 0.4s ease-in-out',
        top: titleBarOffset,
      }}
    >
      {isLoaderActive && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            width: '100%',
            height: 3,
            zIndex: 2,
            backgroundColor: alpha(accentColor, 0.18),
            '& .MuiLinearProgress-bar': { backgroundColor: accentColor },
          }}
        />
      )}

      <Toolbar sx={{ minHeight: '62px !important', height: 72, px: { xs: 2.25, md: 4 } }}>
        <AppHeaderControls handleDrawerOpen={handleDrawerOpen} />
      </Toolbar>
    </AppBar>
  )
}

export default Header
